import {
  SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
  SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
  SERVICE_DESK_FORMILY_ENDUSER_MODEL,
  SERVICE_DESK_FORMILY_TICKET_MODEL,
} from "./serviceDeskConstants";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";
import {
  buildTrackerReportApiParams,
  type TrackerReportFilters,
} from "@sd/lib/trackerReportFilters";

/** React Query key for GET /service_desk/agent_employee_list/ */
export const agentEmployeeListQueryKey = [
  "service-desk",
  "agent_employee_list",
] as const;

/** React Query key for GET /service_desk/project-assigned/?project_id=... */
export function serviceDeskProjectAssignedQueryKey(
  projectId: string | null | undefined
): readonly unknown[] {
  return ["service-desk", "project-assigned", projectId ?? null] as const;
}

/** React Query key for GET /service_desk/employee_list/ */
export const serviceDeskEmployeeListQueryKey = [
  "service-desk",
  "employee_list",
] as const;

/** React Query key for GET /service_desk/priority_listing/ */
export const serviceDeskPriorityListingQueryKey = [
  "service-desk",
  "priority_listing",
] as const;

export const serviceDeskInAppNotificationsQueryKey = [
  "service-desk",
  "in-app-notifications",
] as const;

export const serviceDeskKanbanViewQueryKey = [
  "service-desk",
  "kanban-view",
] as const;

/** GET /service_desk/status_dashboard/ */
export const serviceDeskStatusDashboardQueryKey = [
  "service-desk",
  "status_dashboard",
] as const;

/** GET /service_desk/reports/ — Tracker matrix report */
export const serviceDeskTrackerReportQueryKey = [
  "service-desk",
  "reports",
  "tracker",
] as const;

export function serviceDeskTrackerReportQueryKeyWith(
  filters: TrackerReportFilters
): readonly unknown[] {
  const projectKey =
    normalizeMultiFilterValues(filters.projectFilters).join(",") || "all";
  const dates = filters.createdDateFilter;
  const assigneeKey = filters.assigneeFilter?.trim() || "all";
  return [
    ...serviceDeskTrackerReportQueryKey,
    projectKey,
    dates.from ?? "",
    dates.to ?? "",
    assigneeKey,
  ] as const;
}

export function buildTrackerReportQueryParams(
  filters: TrackerReportFilters
): Record<string, string | boolean> {
  return buildTrackerReportApiParams(filters);
}

export function serviceDeskStatusDashboardQueryKeyWith(
  projectFilters: string[] = [],
  startDate = "",
  endDate = ""
): readonly unknown[] {
  const projectKey =
    normalizeMultiFilterValues(projectFilters).join(",") || "all";
  return [
    ...serviceDeskStatusDashboardQueryKey,
    projectKey,
    startDate,
    endDate,
  ] as const;
}

/** GET /pts_app/project-summary/open-project-list/ */
export const ptsOpenProjectListQueryKey = [
  "pts",
  "open-project-list",
] as const;

/** GET /pts_app/project-task/?project={id}&is_dashboard=true */
export function ptsTaskListQueryKey(projectId: string): readonly unknown[] {
  return ["pts", "task-list", projectId] as const;
}

/** GET /dropdown/PTS Work Type/ */
export const ptsWorkTypeListQueryKey = ["pts", "work-type-list"] as const;

export function serviceDeskKanbanViewQueryKeyWith(
  userId: string | undefined | null,
  projectFilter = "all",
  assigneeFilter = "all",
  createdDateFilter?: { from?: string; to?: string } | null
): readonly unknown[] {
  return [
    ...serviceDeskKanbanViewQueryKey,
    userId ?? null,
    projectFilter,
    assigneeFilter,
    createdDateFilter?.from ?? "",
    createdDateFilter?.to ?? "",
  ] as const;
}

/** Query params for GET /service_desk/kanban-view/ */
export function buildKanbanViewQueryParams(
  empId: string | undefined,
  projectFilter: string,
  assigneeFilter: string,
  unassignedSentinel: string,
  createdDateFilter?: { from?: string; to?: string } | null
): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {};
  const emp = empId?.trim();
  if (emp) {
    params.emp_id = emp;
  }
  if (projectFilter !== "all") {
    params.project = projectFilter;
  }
  if (assigneeFilter !== "all") {
    if (assigneeFilter === unassignedSentinel) {
      params.assigned_to__isnull = true;
    } else {
      params.assigned_to = assigneeFilter;
    }
  }
  const from = createdDateFilter?.from?.trim();
  const to = createdDateFilter?.to?.trim();
  if (from) {
    params.start_date = from;
  }
  if (to) {
    params.end_date = to;
  }
  return params;
}

export const serviceDeskAssetsListQueryKey = [
  "service-desk",
  "assets",
  "list",
] as const;

export function serviceDeskFormilyAddMemberGridQueryKey(
  companyId: string
): readonly unknown[] {
  return [
    "formily-add-member-grid",
    SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
    companyId,
  ] as const;
}

/** React Query key: GET /service_desk/employee_list/?company_name=… */
export function serviceDeskEmployeeListByCompanyQueryKey(
  companyId: string
): readonly unknown[] {
  return ["service-desk", "employee_list", "by-company", companyId] as const;
}

export const serviceDeskFormilyCustomerCompanyGridQueryKey = [
  "formily-customer-company-grid",
  SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
] as const;

export const serviceDeskFormilyTicketGridQueryKey = [
  "formily-ticket-grid",
  SERVICE_DESK_FORMILY_TICKET_MODEL,
] as const;

/** React Query key: GET `/service_desk/ticket_create/` list with filters. */
export const serviceDeskTicketCreateListQueryKey = [
  "service-desk",
  "ticket_create",
  "list",
] as const;

export function serviceDeskTicketCreateListQueryKeyWith(
  filterParams?: Record<string, unknown> | null,
  page?: number,
  pageSize?: number,
  ...scope: readonly unknown[]
): readonly unknown[] {
  return [
    ...serviceDeskTicketCreateListQueryKey,
    ...scope,
    filterParams ?? null,
    page ?? null,
    pageSize ?? null,
  ] as const;
}

/** React Query key: GET …/formily/dynamic-model/ServiceDesk%20Enduser/grid_data/ */
export const serviceDeskFormilyEndUserGridQueryKey = [
  "formily-enduser-grid",
  SERVICE_DESK_FORMILY_ENDUSER_MODEL,
] as const;

export function serviceDeskFormilyEndUserGridQueryKeyWith(
  page: number,
  pageSize: number
): readonly unknown[] {
  return [...serviceDeskFormilyEndUserGridQueryKey, page, pageSize] as const;
}

/** React Query key: GET `/service_desk/ticket_create/{id}/` (read-only detail page). */
export function serviceDeskTicketCreateDetailQueryKey(
  ticketId: string | null | undefined
): readonly unknown[] {
  return ["sd-ticket-created-detail", ticketId ?? null] as const;
}

/** React Query key: GET `/service_desk/ticket_history/?ticket_id=` */
export function serviceDeskTicketHistoryQueryKey(
  ticketId: string | null | undefined
): readonly unknown[] {
  return ["sd-ticket-history", ticketId ?? null] as const;
}

/** React Query key: GET `/service_desk/ticket_notes/?ticket_id=` */
export function serviceDeskTicketNotesQueryKey(
  ticketId: string | null | undefined
): readonly unknown[] {
  return ["sd-ticket-notes", ticketId ?? null] as const;
}

/** React Query key: admin Formily GET ticket by id (detail panel / merge with grid row). */
export function serviceDeskAdminTicketDetailQueryKey(
  ticketId: string | null | undefined
): readonly unknown[] {
  return [
    "sd-admin-ticket-detail",
    SERVICE_DESK_FORMILY_TICKET_MODEL,
    ticketId ?? null,
  ] as const;
}

/**
 * Base ticket-grid query key + optional filters (e.g. `requester`).
 * Tickets page also appends `agent-panel` | `requester-panel` so list vs grid_data caches stay separate; invalidation still uses `serviceDeskFormilyTicketGridQueryKey` prefix.
 */
export function serviceDeskFormilyTicketGridQueryKeyWith(
  filterParams?: Record<string, string | undefined>
): readonly unknown[] {
  const hasFilters =
    filterParams &&
    Object.values(filterParams).some(v => v !== undefined && v !== "");
  if (!hasFilters) {
    return serviceDeskFormilyTicketGridQueryKey;
  }
  return [...serviceDeskFormilyTicketGridQueryKey, filterParams];
}
